// Centralised extraction of a specific, user-readable message from any API
// error. The backend surfaces validation failures in a few different shapes:
//
//   1. Zod `.flatten()`:
//        { message: "Validation failed",
//          errors: { formErrors: string[],
//                    fieldErrors: { basisPeriodStart: ["…must be before…"] } } }
//   2. A plain field map: { errors: { field: "message" | ["message"] } }
//   3. A single string: { message: "…" } or { error: "…" }
//
// Previously call sites read only `data.message`, so a Zod failure showed the
// generic "Validation failed" and hid the actual field message. This helper
// always prefers the most granular message available so the user sees exactly
// what went wrong.

// Humanise a camelCase / snake_case field key into a readable label,
// e.g. "basisPeriodStart" -> "Basis period start".
function humanizeField(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_.-]+/g, " ")
    .trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// Prefix a field's message with its humanised label, unless the message
// already begins with that label (Zod messages like "Basis period start must
// be before…" already carry the context, whereas built-ins like "Required"
// do not).
function labelledMessage(field: string, message: string): string {
  const label = humanizeField(field);
  const msg = message.trim();
  if (!field || msg.toLowerCase().startsWith(label.toLowerCase())) return msg;
  return `${label}: ${msg}`;
}

// Collect every human-readable message out of an `errors` payload. Returns an
// empty array when nothing usable is present so the caller can fall through to
// the top-level message.
function collectValidationMessages(errors: unknown): string[] {
  if (!errors || typeof errors !== "object") return [];
  const out: string[] = [];
  const obj = errors as Record<string, unknown>;

  const formErrors = obj.formErrors;
  const fieldErrors = obj.fieldErrors;
  const isFlattenShape =
    Array.isArray(formErrors) || (!!fieldErrors && typeof fieldErrors === "object");

  if (isFlattenShape) {
    if (Array.isArray(formErrors)) {
      for (const m of formErrors) {
        if (typeof m === "string" && m.trim()) out.push(m.trim());
      }
    }
    if (fieldErrors && typeof fieldErrors === "object") {
      for (const [field, msgs] of Object.entries(fieldErrors as Record<string, unknown>)) {
        const list = Array.isArray(msgs) ? msgs : [msgs];
        for (const m of list) {
          if (typeof m === "string" && m.trim()) out.push(labelledMessage(field, m));
        }
      }
    }
    return out;
  }

  // Plain { field: string | string[] } map (non-Zod validators).
  for (const [field, msgs] of Object.entries(obj)) {
    const list = Array.isArray(msgs) ? msgs : [msgs];
    for (const m of list) {
      if (typeof m === "string" && m.trim()) out.push(labelledMessage(field, m));
    }
  }
  return out;
}

/**
 * Turn any thrown API error into a specific, user-readable message.
 *
 * Order of preference:
 *   1. Field-level validation messages (Zod `fieldErrors` / `formErrors`).
 *   2. Top-level `data.message`.
 *   3. Top-level `data.error`.
 *   4. The Error's own `message`.
 *   5. The supplied `fallback`.
 *
 * Multiple field messages are joined with newlines (safe for `alert`, and for
 * modal bodies that render `white-space: pre-line`).
 *
 * @param err       The caught error (typically an Axios error).
 * @param fallback  Message to use when nothing specific can be extracted.
 *                  Pass "" to preserve legacy `serverMsg ? … : …` conditionals.
 */
export function getApiErrorMessage(
  err: any,
  fallback = "Something went wrong. Please try again."
): string {
  const data = err?.response?.data;

  if (data && typeof data === "object") {
    const validationMsgs = collectValidationMessages((data as any).errors);
    if (validationMsgs.length > 0) {
      // De-duplicate while preserving order.
      return Array.from(new Set(validationMsgs)).join("\n");
    }
    const message = (data as any).message;
    if (typeof message === "string" && message.trim()) return message.trim();
    const error = (data as any).error;
    if (typeof error === "string" && error.trim()) return error.trim();
  }

  if (typeof err?.message === "string" && err.message.trim()) return err.message.trim();
  return fallback;
}
